import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface PurchaseLimit {
  id: string;
  retailer_name: string;
  distributor_name: string;
  current_limit: number;
  used_amount: number;
  available_limit: number;
  utilization_percentage: number;
  last_updated: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface PurchaseLimitSummary {
  totalRetailers: number;
  activeLimits: number;
  maxedOut: number;
  totalLimits: number;
}

@Component({
  selector: 'app-purchase-limits',
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent],
  templateUrl: './purchase-limits.component.html',
  styleUrls: ['./purchase-limits.component.scss']
})
export class PurchaseLimitsComponent implements OnInit {

  private baseUrl = environment.apiBaseUrl;

  purchaseLimits: PurchaseLimit[] = [];

  pagination: Pagination = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  };

  // Overall summary (not page-scoped)
  summary: PurchaseLimitSummary = { totalRetailers: 0, activeLimits: 0, maxedOut: 0, totalLimits: 0 };

  searchTerm = '';
  loading = false;

  constructor(private http: HttpClient) { }

  ngOnInit() {
    this.fetchPurchaseLimitsSummary();
    this.fetchPurchaseLimits();
  }

  fetchPurchaseLimitsSummary() {
    this.http.get<any>(`${this.baseUrl}/admin/purchase-limit/summary`).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {
          this.summary = res.data;
        }
      },
      error: (err) => {
        console.error('Error fetching purchase limits summary', err);
      }
    });
  }

  fetchPurchaseLimits(page: number = 1) {
    this.loading = true;

    let params = new HttpParams()
      .set('page', page)
      .set('limit', this.pagination.limit)
      .set('search', this.searchTerm || '');

    this.http.get<any>(`${this.baseUrl}/admin/purchase-limit`, { params })
      .subscribe({
        next: (res) => {
          const responseData = res.data;

          this.purchaseLimits = responseData.data.map((item: any) => ({
            id: item.id,
            retailer_name: item.retailer_name,
            distributor_name: item.distributors?.length
              ? item.distributors.map((d: any) => d.distributor_name).join(', ')
              : '-',
            current_limit: Number(item.purchase_limit),
            used_amount: Number(item.used_amount),
            available_limit: Number(item.available_limit),
            utilization_percentage: Number(item.utilization_percentage),
            last_updated: item.updated_at
          }));

          this.pagination = {
            total: responseData.pagination.total,
            page: responseData.pagination.page,
            limit: responseData.pagination.limit,
            totalPages: responseData.pagination.totalPages,
            hasNext: responseData.pagination.page < responseData.pagination.totalPages,
            hasPrev: responseData.pagination.page > 1
          };

          // Scroll to top on page change
          window.scrollTo({ top: 0, behavior: 'smooth' });

          this.loading = false;
        },
        error: (err) => {
          console.error('Error fetching purchase limits', err);
          this.loading = false;
        }
      });
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.fetchPurchaseLimits(1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.fetchPurchaseLimits(page);
  }

  editPurchaseLimit(limit: PurchaseLimit) {
    console.log('Editing purchase limit:', limit);
    // implement edit modal or navigation here
  }

  getAvailableAmount(limit: PurchaseLimit): number {
    return limit.available_limit;
  }

  getUtilizationPercentage(limit: PurchaseLimit): number {
    return Math.round(limit.utilization_percentage);
  }

  getUtilizationClass(limit: PurchaseLimit): string {
    const percentage = this.getUtilizationPercentage(limit);
    if (percentage <= 70) {
      return 'utilization-low';
    } else if (percentage <= 90) {
      return 'utilization-medium';
    } else {
      return 'utilization-high';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Summary Cards — sourced from overall summary, not current page

  getTotalRetailers(): number {
    return this.summary.totalRetailers;
  }

  getActiveLimits(): number {
    return this.summary.activeLimits;
  }

  getMaxedOut(): number {
    return this.summary.maxedOut;
  }

  getTotalLimits(): number {
    return this.summary.totalLimits;
  }
}
